import React, { useState, useEffect } from 'react';
import { GeneralParameterDefinition, PersonalParameterDefinition } from '../../types';
import { ParameterLabelWithTooltip } from './Tooltip';

interface BaseParameterInputProps {
  id: string;
  name: string;
  unit?: string;
  description?: string;
  onChange?: (id: string, value: any) => void;
  disabled?: boolean;
  className?: string;
}

interface DateParameterInputProps extends BaseParameterInputProps {
  type: 'date';
  value: string;
}

interface NumberParameterInputProps extends BaseParameterInputProps {
  type: 'number';
  value: number;
  step?: number;
}

interface MeanStdDevParameterInputProps extends BaseParameterInputProps {
  type: 'meanStdDev';
  value: { mean: number; stdDev: number };
  isPercent?: boolean;
}

interface CalculatedParameterInputProps extends BaseParameterInputProps {
  type: 'calculated';
  value: string | number;
}

type ParameterInputProps = 
  | DateParameterInputProps 
  | NumberParameterInputProps 
  | MeanStdDevParameterInputProps 
  | CalculatedParameterInputProps;

/**
 * Generic parameter input component that handles different parameter types
 */
export const ParameterInput: React.FC<ParameterInputProps> = (props) => {
  const { id, name, unit, description, onChange, disabled = false, className = '' } = props;

  // Local state for editing values (to allow display of raw input like "000")
  const [editingValues, setEditingValues] = useState<{[key: string]: string}>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Reset editing values when props change from external source
  useEffect(() => {
    setEditingValues({});
  }, [props.value]);

  const getDisplayValue = (field: string, actualValue: number | string): string => {
    // If this field is being edited, show the raw editing value
    if (focusedField === field && editingValues[field] !== undefined) {
      return editingValues[field];
    }
    // Otherwise show the actual value
    return actualValue.toString();
  };

  const handleChange = (field: string, value: string | number) => {
    const stringValue = value.toString();
    
    // Store the raw editing value
    setEditingValues(prev => ({ ...prev, [field]: stringValue }));

    if (!onChange) return;

    if (props.type === 'date') {
      onChange(id, value);
    } else if (props.type === 'number') {
      onChange(id, parseFloat(stringValue) || 0);
    } else if (props.type === 'meanStdDev') {
      const newValue = { ...props.value };
      if (field === 'mean') {
        newValue.mean = parseFloat(stringValue) || 0;
      } else if (field === 'stdDev') {
        newValue.stdDev = parseFloat(stringValue) || 0;
      }
      onChange(id, newValue);
    }
  };

  const handleFocus = (field: string, currentValue: string) => {
    setFocusedField(field);
    setEditingValues(prev => ({ ...prev, [field]: currentValue }));
  };

  const handleBlur = (field: string) => {
    setFocusedField(null);
    // Clear the editing value to show the normalized version
    setEditingValues(prev => {
      const newState = { ...prev };
      delete newState[field];
      return newState;
    });
  };

  const renderCells = () => {
    switch (props.type) {
      case 'date':
        return (
          <>
            <input
              type="date"
              id={id}
              value={props.value}
              onChange={(e) => handleChange('value', e.target.value)}
              disabled={disabled}
              className="parameter-input date-input"
            />
            <div></div>
            <div>{unit || ''}</div>
          </>
        );

      case 'number':
        return (
          <>
            <input
              type="number"
              id={id}
              value={getDisplayValue('value', props.value)}
              onChange={(e) => handleChange('value', e.target.value)}
              onFocus={(e) => handleFocus('value', e.target.value)}
              onBlur={() => handleBlur('value')}
              disabled={disabled}
              step={props.step || 'any'}
              className="parameter-input number-input"
            />
            <div></div>
            <div>{unit || ''}</div>
          </>
        );

      case 'meanStdDev':
        const step = props.isPercent ? '0.1' : 'any';
        return (
          <>
            <input
              type="number"
              id={`${id}-mean`}
              value={getDisplayValue('mean', props.value.mean)}
              onChange={(e) => handleChange('mean', e.target.value)}
              onFocus={(e) => handleFocus('mean', e.target.value)}
              onBlur={() => handleBlur('mean')}
              disabled={disabled}
              step={step}
              className="parameter-input mean-input"
              placeholder="Mean"
            />
            <input
              type="number"
              id={`${id}-stddev`}
              value={getDisplayValue('stdDev', props.value.stdDev)}
              onChange={(e) => handleChange('stdDev', e.target.value)}
              onFocus={(e) => handleFocus('stdDev', e.target.value)}
              onBlur={() => handleBlur('stdDev')}
              disabled={disabled}
              step={step}
              min="0"
              className="parameter-input stddev-input"
              placeholder="Std Dev"
            />
            <div>{unit || ''}</div>
          </>
        );

      case 'calculated':
        return (
          <>
            <span className="calculated-value" id={`${id}-display`}>
              {props.value}
            </span>
            <div></div>
            <div>{unit || ''}</div>
          </>
        );

      default:
        return (
          <>
            <div>Unsupported type</div>
            <div></div>
            <div></div>
          </>
        );
    }
  };

  return (
    <div className={`parameter-row ${className}`}>
      {description ? (
        <ParameterLabelWithTooltip
          label={name}
          description={description}
          htmlFor={id}
        />
      ) : (
        <label htmlFor={id} className="parameter-label">
          {name}:
        </label>
      )}
      {renderCells()}
    </div>
  );
};

/**
 * Specific component for general parameters
 */
interface GeneralParameterInputProps {
  parameter: GeneralParameterDefinition;
  value: any;
  onChange: (id: string, value: any) => void;
  disabled?: boolean;
}

export const GeneralParameterInput: React.FC<GeneralParameterInputProps> = ({
  parameter,
  value,
  onChange,
  disabled = false
}) => {
  const getInputProps = (): ParameterInputProps => {
    const baseProps = {
      id: parameter.id,
      name: parameter.name,
      unit: parameter.unit,
      description: parameter.description,
      onChange,
      disabled
    };

    if (parameter.type === 'date') {
      // Extract the actual date string from the structured value
      const dateValue = value && typeof value === 'object' && 'value' in value 
        ? value.value 
        : value || parameter.defaultValue || '';
      return {
        ...baseProps,
        type: 'date',
        value: dateValue
      };
    } else if (parameter.type === 'meanStdDev') {
      // Handle structured meanStdDev values
      const meanStdDevValue = value && typeof value === 'object' && 'mean' in value
        ? { mean: value.mean, stdDev: value.stdDev }
        : value || { mean: parameter.defaultMean || 0, stdDev: parameter.defaultStdDev || 0 };
      return {
        ...baseProps,
        type: 'meanStdDev',
        value: meanStdDevValue,
        isPercent: parameter.unit === '%'
      };
    }

    // Fallback
    return {
      ...baseProps,
      type: 'calculated',
      value: 'Unknown type'
    };
  };

  return <ParameterInput {...getInputProps()} />;
};

/**
 * Specific component for personal parameters
 */
interface PersonalParameterInputProps {
  parameter: PersonalParameterDefinition;
  value: any;
  onChange: (id: string, value: any) => void;
  disabled?: boolean;
  personId: string;
}

export const PersonalParameterInput: React.FC<PersonalParameterInputProps> = ({
  parameter,
  value,
  onChange,
  disabled = false,
  personId
}) => {
  const id = `${personId}-${parameter.id}`;
  
  // Local state for editing values
  const [editingValues, setEditingValues] = useState<{[key: string]: string}>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  // Reset editing values when props change from external source
  useEffect(() => {
    setEditingValues({});
  }, [value]);

  const getDisplayValue = (field: string, actualValue: number | string): string => {
    // If this field is being edited, show the raw editing value
    if (focusedField === field && editingValues[field] !== undefined) {
      return editingValues[field];
    }
    // Otherwise show the actual value
    return actualValue.toString();
  };
  
  const handleChange = (field: string, val: string | number) => {
    const stringValue = val.toString();
    
    // Store the raw editing value
    setEditingValues(prev => ({ ...prev, [field]: stringValue }));

    if (parameter.type === 'date') {
      onChange(parameter.id, val);
    } else if (parameter.type === 'number') {
      onChange(parameter.id, parseFloat(stringValue) || 0);
    } else if (parameter.type === 'meanStdDev') {
      const currentValue = value || { mean: 0, stdDev: 0 };
      const newValue = { ...currentValue };
      if (field === 'mean') {
        newValue.mean = parseFloat(stringValue) || 0;
      } else if (field === 'stdDev') {
        newValue.stdDev = parseFloat(stringValue) || 0;
      }
      onChange(parameter.id, newValue);
    }
  };

  const handleFocus = (field: string, currentValue: string) => {
    setFocusedField(field);
    setEditingValues(prev => ({ ...prev, [field]: currentValue }));
  };

  const handleBlur = (field: string) => {
    setFocusedField(null);
    // Clear the editing value to show the normalized version
    setEditingValues(prev => {
      const newState = { ...prev };
      delete newState[field];
      return newState;
    });
  };

  // Return just the input elements, no wrapper
  switch (parameter.type) {
    case 'date':
      return (
        <input
          type="date"
          id={id}
          value={value || parameter.defaultValue || ''}
          onChange={(e) => handleChange('value', e.target.value)}
          disabled={disabled}
          className="parameter-input date-input"
        />
      );

    case 'number':
      const step = parameter.unit === 'years' ? 1 : parameter.unit === '$' ? 100 : undefined;
      const numberValue = value !== undefined ? value : parameter.defaultValue || 0;
      return (
        <input
          type="number"
          id={id}
          value={getDisplayValue('value', numberValue)}
          onChange={(e) => handleChange('value', e.target.value)}
          onFocus={(e) => handleFocus('value', e.target.value)}
          onBlur={() => handleBlur('value')}
          disabled={disabled}
          step={step}
          className="parameter-input number-input"
        />
      );

    case 'meanStdDev':
      const currentValue = value || { mean: parameter.defaultMean || 0, stdDev: parameter.defaultStdDev || 0 };
      const stepVal = parameter.unit === '%' ? '0.1' : 'any';
      return (
        <div className="mean-stddev-inputs">
          <input
            type="number"
            id={`${id}-mean`}
            value={getDisplayValue('mean', currentValue.mean)}
            onChange={(e) => handleChange('mean', e.target.value)}
            onFocus={(e) => handleFocus('mean', e.target.value)}
            onBlur={() => handleBlur('mean')}
            disabled={disabled}
            step={stepVal}
            className="parameter-input mean-input"
            placeholder="Mean"
          />
          <input
            type="number"
            id={`${id}-stddev`}
            value={getDisplayValue('stdDev', currentValue.stdDev)}
            onChange={(e) => handleChange('stdDev', e.target.value)}
            onFocus={(e) => handleFocus('stdDev', e.target.value)}
            onBlur={() => handleBlur('stdDev')}
            disabled={disabled}
            step={stepVal}
            min="0"
            className="parameter-input stddev-input"
            placeholder="Std Dev"
          />
        </div>
      );

    case 'calculated':
      return (
        <span className="calculated-value" id={`${id}-display`}>
          {value || 'Calculating...'}
        </span>
      );

    default:
      return <div>Unknown type</div>;
  }
};
